import type { QueryClient } from '@tanstack/react-query';

import type { AlertsStreamFrame, IAlert } from '../../alert-kinds/index.js';

/**
 * The person's alerts in a site, as the client keeps them
 * (docs/specs/2026-10-18-alerts-design.md, D2). There is no list to ask the
 * server for: the site's notifications stream relays Trusstor's alert stream,
 * whose first frame of each connection (`alerts.reset`) is the whole list and
 * each later one (`alerts.upsert`) more of it. The client merges, exactly as
 * Trusstor's own web app does.
 *
 * Kept in the query cache, under a root of its own: the bell's "ask again"
 * invalidates `['notifications']`, and this entry has nothing to refetch.
 */
export interface IAlertsState {
  /** Newest first. */
  alerts: IAlert[];
  /** Moves on every reset: an arrival is only ever counted within one. */
  generation: number;
}

export const alertsKeys = {
  list: (siteId: string) => ['alerts', siteId] as const,
};

const newestFirst = (alerts: IAlert[]): IAlert[] => [...alerts].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

/** The list after one frame: a reset replaces it; an upsert merges by id,
 *  drops what it names as removed, and marks what it names as viewed. */
export function applyAlertsFrame(state: IAlertsState | undefined, frame: AlertsStreamFrame): IAlertsState {
  if (frame.channel === 'alerts.reset' || !state) {
    return { alerts: newestFirst(frame.data.alerts), generation: (state?.generation ?? 0) + 1 };
  }
  const { alerts, removed = [], viewed = [] } = frame.data;
  const byId = new Map(state.alerts.map((a) => [a.id, a]));
  for (const alert of alerts) byId.set(alert.id, alert);
  for (const id of removed) byId.delete(id);
  const seen = viewed === 'all' ? null : new Set(viewed);
  for (const [id, alert] of byId) {
    if (!alert.viewed && (seen === null || seen.has(id))) byId.set(id, { ...alert, viewed: true });
  }
  return { alerts: newestFirst([...byId.values()]), generation: state.generation };
}

/**
 * What `next` holds that `previous` did not, and that is newer than anything
 * `previous` held — within one connection's list. The first list, and every
 * reset, is a baseline. "Newer" matters because Trusstor can send a live
 * alert ahead of a connection's full list: that alert becomes the reset, and
 * the list behind it holds only older alerts — none of them arrivals.
 */
export function newAlertArrivals(previous: IAlertsState | undefined, next: IAlertsState): IAlert[] {
  if (!previous || previous.generation !== next.generation) return [];
  const known = new Set(previous.alerts.map((a) => a.id));
  const newest = previous.alerts.reduce((max, a) => (a.at > max ? a.at : max), '');
  return next.alerts.filter((a) => !known.has(a.id) && a.at > newest);
}

/** A frame from the stream, into the cache of the site it names. */
export function receiveAlertsFrame(queryClient: QueryClient, frame: AlertsStreamFrame): void {
  queryClient.setQueryData<IAlertsState>(alertsKeys.list(frame.data.siteId), (state) => applyAlertsFrame(state, frame));
}

/** The list with one alert changed — or, with `null`, gone — as the person's
 *  own action leaves it before the server confirms. */
export function patchAlerts(
  state: IAlertsState | undefined,
  change: (alert: IAlert) => IAlert | null,
): IAlertsState | undefined {
  if (!state) return state;
  const alerts = state.alerts.flatMap((a) => {
    const changed = change(a);
    return changed ? [changed] : [];
  });
  return { ...state, alerts };
}
